import { useExitFormDialog } from "@dashboard/components/Form/useExitFormDialog";
import { MetadataFormData } from "@dashboard/components/Metadata";
import { CategoryDetailsFragment } from "@dashboard/graphql";
import useForm, { CommonUseFormResult, FormChange } from "@dashboard/hooks/useForm";
import useHandleFormSubmit from "@dashboard/hooks/useHandleFormSubmit";
import { mapMetadataItemToInput } from "@dashboard/utils/maps";
import getMetadata from "@dashboard/utils/metadata/getMetadata";
import useMetadataChangeTrigger from "@dashboard/utils/metadata/useMetadataChangeTrigger";
import {
  ExtendedDescriptionRichTextContext,
  RichTextContext,
  RichTextContextValues,
} from "@dashboard/utils/richText/context";
import { RichTextDocument } from "@dashboard/utils/richText/types";
import useRichText from "@dashboard/utils/richText/useRichText";
import React, { useEffect } from "react";

export interface CategoryUpdateFormData extends MetadataFormData {
  backgroundImageAlt: string;
  name: string;
  slug: string;
  seoTitle: string;
  seoDescription: string;
}
export interface CategoryUpdateData extends CategoryUpdateFormData {
  description: RichTextDocument | null;
}

interface CategoryUpdateHandlers {
  changeMetadata: FormChange;
}

export interface UseCategoryUpdateFormResult extends CommonUseFormResult<CategoryUpdateData> {
  handlers: CategoryUpdateHandlers;
}

export interface CategoryUpdateFormProps {
  children: (props: UseCategoryUpdateFormResult) => React.ReactNode;
  category: CategoryDetailsFragment | undefined | null;
  onSubmit: (data: CategoryUpdateData) => Promise<any[]>;
  disabled: boolean;
}

const getInitialData = (category: CategoryDetailsFragment | undefined | null) => ({
  backgroundImageAlt: category?.backgroundImage?.alt || "",
  metadata: category?.metadata?.map(mapMetadataItemToInput),
  name: category?.name || "",
  privateMetadata: category?.privateMetadata?.map(mapMetadataItemToInput),
  seoDescription: category?.seoDescription || "",
  seoTitle: category?.seoTitle || "",
  slug: category?.slug || "",
});

const EXTENDED_DESCRIPTION_METADATA_KEY = "extended_description";

export function useCategoryUpdateForm(
  category: CategoryDetailsFragment | undefined | null,
  onSubmit: (data: CategoryUpdateData) => Promise<any[]>,
  disabled: boolean,
): UseCategoryUpdateFormResult & {
  richText: RichTextContextValues;
  extendedDescriptionRichText: RichTextContextValues;
} {
  const {
    handleChange,
    data: formData,
    triggerChange,
    formId,
    setIsSubmitDisabled,
  } = useForm(getInitialData(category), undefined, { confirmLeave: true });
  const handleFormSubmit = useHandleFormSubmit({
    formId,
    onSubmit,
  });
  const { setExitDialogSubmitRef } = useExitFormDialog({
    formId,
  });
  const richText = useRichText({
    initial: category?.description,
    loading: !category,
    triggerChange,
  });
  const extendedDescriptionRichText = useRichText({
    initial:
      category?.metadata?.find(m => m.key === EXTENDED_DESCRIPTION_METADATA_KEY)?.value ?? null,
    loading: !category,
    triggerChange,
  });
  const {
    isMetadataModified,
    isPrivateMetadataModified,
    makeChangeHandler: makeMetadataChangeHandler,
  } = useMetadataChangeTrigger();
  const changeMetadata = makeMetadataChangeHandler(handleChange);
  const data = {
    ...formData,
    description: null,
  } as CategoryUpdateData;
  // Need to make it function to always have description.current up to date
  const getData = async (): Promise<CategoryUpdateData> =>
    ({
      ...formData,
      description: await richText.getValue(),
    }) as CategoryUpdateData;
  const getSubmitData = async (): Promise<CategoryUpdateData> => {
    const baseMetadata = getMetadata(data, isMetadataModified, isPrivateMetadataModified);
    let finalMetadata = baseMetadata.metadata;

    if (extendedDescriptionRichText.isDirty) {
      const extendedDescriptionValue = await extendedDescriptionRichText.getValue();
      const currentMetadata = finalMetadata ?? data.metadata ?? [];
      const idx = currentMetadata.findIndex(m => m.key === EXTENDED_DESCRIPTION_METADATA_KEY);
      const newEntry = {
        key: EXTENDED_DESCRIPTION_METADATA_KEY,
        value: JSON.stringify(extendedDescriptionValue),
      };

      finalMetadata =
        idx >= 0
          ? [...currentMetadata.slice(0, idx), newEntry, ...currentMetadata.slice(idx + 1)]
          : [...currentMetadata, newEntry];
    }

    return {
      ...(await getData()),
      ...baseMetadata,
      ...(finalMetadata !== undefined && { metadata: finalMetadata }),
    } as CategoryUpdateData;
  };
  const submit = async () => handleFormSubmit(await getSubmitData());

  useEffect(() => setExitDialogSubmitRef(submit), [submit]);
  setIsSubmitDisabled(disabled);

  return {
    change: handleChange,
    data,
    handlers: {
      changeMetadata,
    },
    submit,
    isSaveDisabled: disabled,
    richText,
    extendedDescriptionRichText,
  };
}

const CategoryUpdateForm = ({
  children,
  category,
  onSubmit,
  disabled,
}: CategoryUpdateFormProps) => {
  const { richText, extendedDescriptionRichText, ...props } = useCategoryUpdateForm(
    category!,
    onSubmit,
    disabled,
  );

  return (
    <form onSubmit={props.submit}>
      <RichTextContext.Provider value={richText}>
        <ExtendedDescriptionRichTextContext.Provider value={extendedDescriptionRichText}>
          {children(props)}
        </ExtendedDescriptionRichTextContext.Provider>
      </RichTextContext.Provider>
    </form>
  );
};

CategoryUpdateForm.displayName = "CategoryUpdateForm";
export default CategoryUpdateForm;
